<template>
  <div class="client applications-list-toolbar" v-if="phase && phase.name">
    <v-divider />
    <v-toolbar flat :color="themes.client.light.toolbar">
      <v-toolbar-items>
        <v-menu
          offset-y
          v-if="isManager"
          v-show="phase.name !== 'Withdrawn'"
          v-model="bulkActionsVisible"
          :close-on-content-click="false"
        >
          <v-btn flat slot="activator">
            Bulk actions
            <v-icon right>mdi-chevron-down</v-icon>
          </v-btn>
          <v-card flat>
            <v-subheader>
              {{ selected.length }}
              {{ selected.length | pluralize("Application") }} selected
            </v-subheader>
            <v-list>
              <v-list-tile @click="selectAll" :disabled="allSelected">
                <v-list-tile-content>
                  <v-list-tile-title class="subheading">
                    Select All
                  </v-list-tile-title>
                </v-list-tile-content>
                <v-list-tile-action :style="allSelected ? 'opacity: 0.5;' : ''">
                  <v-icon>mdi-checkbox-multiple-marked-outline</v-icon>
                </v-list-tile-action>
              </v-list-tile>
              <v-list-tile @click="deselectAll" :disabled="!selected[0]">
                <v-list-tile-content>
                  <v-list-tile-title class="subheading"
                    >Deselect All</v-list-tile-title
                  >
                </v-list-tile-content>
                <v-list-tile-action
                  :style="!selected[0] ? 'opacity: 0.5;' : ''"
                >
                  <v-icon>mdi-checkbox-multiple-blank-outline</v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
            <v-divider />
            <ApplicationEventsList
              :applications="selected"
              include-regret
              @done="done"
            />
          </v-card>
        </v-menu>
      </v-toolbar-items>
      <v-spacer />
      <v-toolbar-items>
        <v-tooltip bottom v-if="isManager">
          <v-btn
            flat
            slot="activator"
            :disabled="!opportunity.downloadToken"
            :href="`${$root.$api}/client/opportunities/${opportunity.id}/applications.xlsx?token=${opportunity.downloadToken}`"
            target="_blank"
          >
            <span v-if="!iconsOnly">Export All</span>
            <v-icon
              :right="!iconsOnly"
              medium
              class="green--text text--darken-3"
            >
              mdi-file-excel-box
            </v-icon>
          </v-btn>
          <span>Export all applications to Excel</span>
        </v-tooltip>

        <ApplicationsExportSelector
          v-if="isManager"
          :opportunity="opportunity"
          :value="selected"
        >
          <v-btn slot="activator" flat :disabled="!selected[0]">
            <span v-if="!iconsOnly">Export</span>
            <v-badge :value="!!selected[0]" :color="themes.client.light.chip">
              <span slot="badge">{{ selected.length }}</span>
              <v-icon
                :right="!iconsOnly"
                medium
                class="red--text text--darken-3"
              >
                mdi-file-pdf-box
              </v-icon>
            </v-badge>
          </v-btn>
        </ApplicationsExportSelector>

        <v-tooltip bottom>
          <v-menu
            offset-y
            bottom
            slot="activator"
            v-model="sharing"
            :close-on-content-click="false"
            :disabled="!selected[0]"
          >
            <v-btn
              flat
              small
              slot="activator"
              :color="themes.client.light.primaryActionButton"
              :disabled="!selected[0]"
              @click.stop="sharing = !sharing"
            >
              <span v-if="!iconsOnly">Share</span>
              <v-badge :value="!!selected[0]" :color="themes.client.light.chip">
                <span slot="badge">{{ selected.length }}</span>
                <v-icon :right="!iconsOnly" medium
                  >mdi-share-variant-outline</v-icon
                >
              </v-badge>
            </v-btn>
            <FeedbackRequestsForm
              :applications-ids="selected"
              @close="sharing = false"
            />
          </v-menu>
          <span>Share Candidate</span>
        </v-tooltip>

        <v-menu offset-y>
          <v-tooltip bottom slot="activator">
            <v-btn slot="activator" flat class="pr-1" style="min-width: 0px">
              <span v-if="!iconsOnly">Sort by </span>
              <v-icon>{{ sortBy.icon }}</v-icon>
            </v-btn>
            <span>Sort applications</span>
          </v-tooltip>
          <v-list>
            <v-list-tile
              @click="changeSortBy(sort)"
              v-for="(sort, i) in sortValues"
              :key="`sort-${i}`"
            >
              <v-list-tile-content>
                <v-list-tile-title>
                  <span class="caption" v-if="iconsOnly">Sort by </span>
                  <span class="caption">{{ sort.label }}</span>
                </v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-icon>{{ sort.icon }}</v-icon>
              </v-list-tile-action>
            </v-list-tile>
          </v-list>
        </v-menu>

        <v-tooltip bottom slot="activator">
          <v-btn
            slot="activator"
            flat
            @click="changeSortDirection"
            class="pl-1"
            style="min-width: 0px"
          >
            <v-icon medium v-if="sortBy.desc" key="sort-asc"
              >mdi-sort-descending</v-icon
            >
            <v-icon medium v-else key="sort-desc">mdi-sort-ascending</v-icon>
          </v-btn>
          <span>Change sorting direction</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn slot="activator" flat @click="filtering = !filtering">
            <span v-if="!iconsOnly">Filter</span>
            <v-icon :right="!iconsOnly" medium>mdi-filter</v-icon>
          </v-btn>
          <span>Set application filters</span>
        </v-tooltip>
      </v-toolbar-items>
    </v-toolbar>
    <ApplicationsFilter v-model="filtering" />
    <v-divider />
  </div>
</template>

<script>
import { mapState } from "vuex";

// Components
import ApplicationEventsList from "@/components/client/applications/ApplicationEventsList";
import ApplicationsExportSelector from "@/components/client/applications/ApplicationsExportSelector";
import ApplicationsFilter from "@/components/client/applications/ApplicationsFilter";
import FeedbackRequestsForm from "@/components/client/feedback_requests/FeedbackRequestsForm";

export default {
  props: { applications: Array, phase: Object },
  components: {
    ApplicationEventsList,
    ApplicationsExportSelector,
    ApplicationsFilter,
    FeedbackRequestsForm,
  },
  data() {
    return {
      bulkActionsVisible: false,
      filtering: false,
      sharing: false,
      sortBy: {
        value: "createdAt",
        label: "Application Date",
        icon: "mdi-calendar",
        desc: false,
      },
      sortValues: [
        {
          value: "createdAt",
          label: "Application Date",
          icon: "mdi-calendar",
          desc: false,
        },
        {
          value: "profile.assessmentAggregate",
          label: "Assessment Results",
          icon: "mdi-account-search",
          desc: true,
        },
        {
          value: "lastApplicationEventAt",
          label: "Last Moved",
          icon: "mdi-account-arrow-right",
          desc: true,
        },
        {
          value: "profile.lastName.sort",
          label: "Last Name",
          icon: "mdi-alphabetical",
          desc: false,
        },
        {
          value: "viewedAt",
          label: "Last Viewed",
          icon: "mdi-eye",
          desc: true,
        },
        {
          value: "completion",
          label: "Profile Completion",
          icon: "mdi-timelapse",
          desc: true,
        },
        {
          value: "applicationRating.value",
          label: "Star Rating",
          icon: "mdi-star",
          desc: true,
        },
        {
          value: "unreadChats",
          label: "Unread Messages",
          icon: "mdi-message",
          desc: true,
        },
      ],
    };
  },
  computed: {
    opportunity() {
      return this.$store.getters["client/opportunities/find"]();
    },

    ...mapState("client/applications", ["selected"]),

    allSelected() {
      return (
        _.intersection(
          this.applications.map((a) => a.id),
          this.selected
        ).length === this.applications.length
      );
    },
    iconsOnly() {
      return this.$vuetify.breakpoint.mdAndDown;
    },
    isManager() {
      return this.$store.getters["client/opportunities/currentIsManager"]();
    },
  },
  methods: {
    selectAll() {
      return this.$store.commit(
        "client/applications/select",
        this.applications.map((c) => c.id)
      );
    },
    deselectAll() {
      this.bulkActionsVisible = false;
      return this.$store.commit("client/applications/clearSelected");
    },
    copiedToast(value) {
      this.$store.dispatch("snackbarShow", `Copied ${value}.`);
    },

    done(phase = {}) {
      this.deselectAll();
    },

    changeSortDirection() {
      this.sortBy.desc = !this.sortBy.desc;
      this.$store.dispatch("client/applications/updateSort", {
        by: this.sortBy.value,
        descending: this.sortBy.desc,
      });
    },

    changeSortBy(by) {
      this.sortBy = by;
      this.$store.dispatch("client/applications/updateSort", {
        by: by.value,
        descending: false,
      });
    },
  },
};
</script>
